import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Form } from 'react-router-dom';
import Sidebar from './Sidebar';
import { LoaderWrapper } from '../../components/index';

// Import API services
import {
  submitApplication} from '../../services/api';

// Import all DO sections
import SubmittedApplicationSection from './application/SubmittedApplication';
import ApplicationDetailView from './application/ApplicationDetailView';
import AssessorProfile from './application/AssessorProfile';
import AssessorBiodata from './application/AssessorBioData';
import AssessmentDashboard from './application/AssessmentDashboard';
import Checklist from './application/Checklist';
import Form9 from './application/Form9';
import TADA from './application/TADA';
import NAF6 from './application/NAF6';
import NAF6_New from './application/NAF6_new2';
import AssessorDetails from './application/AssessorDetails';
import TestWitness from './application/TestWitness';
import EquipmentDetails from './application/EquipmentDetails';
import NAF1 from './application/NAF1';
import Feedback from './application/Feedback';
import ScopeWitness from './application/ScopeWitness';
import ScopeWitnessCalibration from './application/ScopeWitnessCalibration';
import Form217Preview from './AssessmentReport/Form217Report';
import NCMainReport from './AssessmentReport/NCMainReport';
import NCApplicationChecklist from './NCApplicationChecklist';
import NCReply from './application/NCReply';  
import NCApplicationFinding from './AssessmentReport/NCApplicationFinding';
import AssessorNCClarification from './application/AssessorNCClarification';
import Form73Calibration from './AssessmentReport/Form73Calibration';
import AssessorLogisticDetails from './application/AssessorLogisticDetails';
import ScopeWitnessTesting from './application/ScopeWitnessTesting';

const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const AssessorLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentSection, setCurrentSection] = useState('submitted-applications');
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labData, setLabData] = useState(null);
  const [appId, setAppId] = useState(null);
  const [showDeclaration, setShowDeclaration] = useState(false);

  // Section configuration
  const sections = useMemo(() => [
    {
      id: 'submitted-applications',
      title: 'Submitted Applications',
      component: SubmittedApplicationSection,
      path: '/main/assessor/application-assessment',
    },
    {
      id: 'applications',
      title: 'Applications',
      component: SubmittedApplicationSection,
      path: '/main/assessor/applications',
    },
    {
      id: 'application-detail',
      title: 'Application Detail',
      component: ApplicationDetailView,
      path: '/main/assessor/applications/detail',
    },
    {
      id: 'assessor-profile',
      title: 'Assessor Profile',
      component: AssessorProfile,
      path: '/main/assessor/assessor-profile',
    },
    {
      id: 'assessor-bio-data',
      title: 'Assessor Bio Data',
      component: AssessorBiodata,
      path: '/main/assessor/assessor-bio-data',
    },
    {
      id: 'assessment-dashboard',
      title: 'Assessment Dashboard',
      component: AssessmentDashboard,
      path: '/main/assessor/assessment-dashboard',
    },
    {
      id: 'checklist',
      title: 'Checklist',
      component: Checklist,
      path: '/main/assessor/checklist',
    },
    {
      id: 'TADA',
      title: 'Travel Allowance and Daily Allowance',
      component: TADA,
      path: '/main/assessor/TADA',
    },
    {
      id: 'NAF6',
      title: 'NAF6',
      component: NAF6,
      path: '/main/assessor/NAF6',
    },
     {
      id: 'NAF6-new',
      title: 'NAF6',
      component: NAF6_New,
      path: '/main/assessor/NAF6-new',
    },
    {
      id: 'Form9',
      title: 'Form9',
      component: Form9,
      path: '/main/assessor/form9',
    },
    { id: 'Test-Witness', 
      title: 'Test Witness',
      component: TestWitness,
      path: '/main/assessor/test-witness'
     },
    {
      id: 'equipment-details',
      title: 'Equipment Details',
      component: EquipmentDetails,
      path: '/main/assessor/equipment-details',
    },
    {
      id: 'Assessor-Details',
      title: 'Assessor Details',
      component: AssessorDetails,
      path: '/main/assessor/assessor-details',
    },
    {
      id: 'NAF1',
      title: 'NAF1',
      component: NAF1,
      path: '/main/assessor/naf1',
    },
    {
      id: 'Feedback',
      title: 'Feedback',
      component: Feedback,
      path: '/main/assessor/feedback',
    },
    {
      id: 'Scope-Witness',
      title: 'Scope Witness',
      component: ScopeWitness,
      path: '/main/assessor/scope-witness',
    },
    {
      id: 'Scope-Witness-Calibration',
      title: 'Scope Witness Calibration',
      component: ScopeWitnessCalibration,
      path: '/main/assessor/scope-witness-calibration',
    },
    {
      id: 'Form217-Preview',
      title: 'Form217 Preview',
      component: Form217Preview,
      path: '/main/assessor/form217-preview',
    },
    {
      id: 'Form73-Calibration',
      title: 'Form73 Calibration',
      component: Form73Calibration,
      path: '/main/assessor/form73-calibration',
    },
    {
      id: 'NC Reply',
      title: 'NC Reply',
      component: NCReply,
      path: '/main/assessor/nc-reply',
    },
    {
      id: 'NC Report',
      title: 'NC Report',
      component: NCMainReport,
      path: '/main/assessor/nc-report',
    },
    {
      id: 'NC Application',
      title: 'NC Application',
      component: NCApplicationChecklist,
      path: '/main/assessor/nc-application',
    },
    {
      id: 'NC Application Finding',
      title: 'NC Application Finding',
      component: NCApplicationFinding,
      path: '/main/assessor/nc-application-finding',
    },
    {
      id: 'Assessor NC Clarification',
      title: 'NC Clarification',
      component: AssessorNCClarification,
      path: '/main/assessor/assessor-nc-clarification',
    },
    {
      id: 'Assessor-Logistic-Details',
      title: 'Assessor Logistic Details',
      component: AssessorLogisticDetails,
      path: '/main/assessor/assessor-logistic-details',
    },
    {
      id: 'Scope-Witness-Testing',
      title: 'Scope Witness Testing',
      component: ScopeWitnessTesting,
      path: '/main/assessor/scope-witness-testing',
    }
  ], []);

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find(s => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);



  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };

    setFormData(updatedFormData);

  };



  const navigateToSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const getCurrentSectionIndex = () => {
    return sections.findIndex(s => s.id === currentSection);
  };

  const handlePreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      navigateToSection(sections[currentIndex - 1].id);
    }
  };

  // Submit complete application
  const submitCompleteApplication = async () => {
    try {
      setLoading(true);

      if (!appId) {
        setError('No application ID found. Please refresh the page.');
        return;
      }

      const payload = {
        appId: appId,
        formData: formData,
        labTypeId: 3, // Medical lab type
        completedSections: completedSections,
      };

      await submitApplication(payload);

      // Show success message and redirect
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/main/dashboard'); // or wherever you want to redirect after submission
      }, 2000);

    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < sections.length - 1) {
      navigateToSection(sections[currentIndex + 1].id);
    } else {
      // Last section - submit application
      submitCompleteApplication();
    }
  };

  const renderCurrentSection = () => {
    console.log('🔍 DoLayout renderCurrentSection:', {
      currentSection,
      pathname: location.pathname,
      availableSections: sections.map(s => ({ id: s.id, path: s.path }))
    });

    const section = sections.find(s => s.id === currentSection);
    if (!section) {
      console.log('❌ Section not found for currentSection:', currentSection);
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Section not found: {currentSection}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current path: {location.pathname}
          </Typography>
        </Box>
      );
    }

    console.log('✅ Rendering section:', section.title, section.component.name);
    const SectionComponent = section.component;

    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
      />
    );
  };

  const currentSectionIndex = getCurrentSectionIndex();
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', height: '100vh', pt: `${APP_HEADER_HEIGHT}px` }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={currentSectionIndex}
          onSectionClick={(item) => navigate(item.path)}
          showDeclaration={showDeclaration}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: 'relative',
        }}
      >
        {/* Toggle Sidebar Button for Desktop when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: '100%' }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper
              loading={true}
              type="page"
              message="Loading application data..."
            />
          )}

          <Paper sx={{ minHeight: 'calc(100vh - 280px)', mb: 3 }}>
            {renderCurrentSection()}
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper
              loading={true}
              type="inline"
              message="Saving form data..."
              size="small"
            />
          )}

        </Container>
      </Box>


      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessorLayout;